import { useEffect, useState } from 'react';

import { Divider, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';

import { PageTitle } from '~/components/common';
import { DeliveryAgencyExcelDownloadModal } from '~/components/delivery-agency';
import {
  DeliveryAgencyTrackingCreateShippingLabelModal,
  DeliveryAgencyTrackingDetailModal,
  DeliveryAgencyTrackingFilter,
  DeliveryAgencyTrackingTable,
} from '~/components/delivery-agency/tracking';
import { PageWrapper } from '~/components/layout';

import { useDownloadReady, useModal, useToggle } from '~/hooks/common';
import {
  useGetShippingLabels,
  useGetShippingLabelsSearchConditions,
  useGetShippingLabelsShippingLabelId,
  usePostShippingLabelsCancel,
  usePostShippingLabelsDownload,
  usePostShippingLabelsPrint,
} from '~/hooks/delivery-agency/api';
import { addIf, renderFilterErrorMessage } from '~/utils/common';
import { formatDate, getDateByOffset } from '~/utils/date';

import {
  BREAD_CRUMB,
  DELIVERY_AGENCY,
  DELIVERY_AGENCY_SHIPPING_LABEL_STATUS,
  DELIVERY_AGENCY_TRACKING_PRINT_STATUS,
  INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
  INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  OPTION_ALL,
  PATH,
  TOAST_MESSAGE,
  type TypeDeliveryAgencyShippingLabelStatus,
} from '~/constants';

import type {
  TypeDeliveryAgencyShippingLabel,
  TypeDeliveryAgencyTrackingFilterValues,
  TypeDeliveryAgencyTrackingFilterValuesStatus,
  TypeGetShippingLabelsParams,
  TypePostShippingLabelsDownloadParams,
} from '~/types/delivery-agency.types';

const INITIAL_SEARCH_FILTER_VALUES: TypeDeliveryAgencyTrackingFilterValues = {
  keywordType: null,
  keyword: '',
  deliveryDate: getDateByOffset(1, 'day'),
  trackingStatus: OPTION_ALL,
  trackingPrintStatus: OPTION_ALL,
  deliveryType: OPTION_ALL,
  courier: OPTION_ALL,
  pagination: {
    currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
    totalCount: 0,
    pageSize: INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  },
};

const DeliveryAgencyTrackingPage = () => {
  const { requestCodes, options: searchFilterOptions } = useGetShippingLabelsSearchConditions();
  const { requestShippingLabels } = useGetShippingLabels();
  const { requestShippingLabelDetail, responseData: trackingDetailData } = useGetShippingLabelsShippingLabelId();
  const { requestShippingLabelsPrint } = usePostShippingLabelsPrint();
  const { requestDeliveryCancel } = usePostShippingLabelsCancel();
  const { requestShippingLabelsDownload } = usePostShippingLabelsDownload();

  const [searchFilterValues, setSearchFilterValues] = useState<TypeDeliveryAgencyTrackingFilterValues>({
    ...INITIAL_SEARCH_FILTER_VALUES,
  });
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] =
    useState<TypeDeliveryAgencyTrackingFilterValuesStatus>({
      keyword: 'default',
      deliveryDate: 'default',
    });

  const [trackingData, setTrackingData] = useState<TypeDeliveryAgencyShippingLabel[] | null>(null);
  const [selectedItems, setSelectedItems] = useState<TypeDeliveryAgencyShippingLabel[]>([]);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });
  const { isDownloadReady, markDownloadReady, checkFilterChange, searchedFilter } = useDownloadReady({
    initialFilter: INITIAL_SEARCH_FILTER_VALUES,
  });

  const trackingDetailModal = useModal();

  const trackingExcelDownloadModal = useModal();

  const createShippingLabelModal = useModal();

  useEffect(() => {
    requestCodes({
      onSuccess: (data) => {
        setSearchFilterValues((prev) => ({
          ...prev,
          keywordType: data.searchKeywordTypes[0] ?? null,
        }));
      },
      onError: (error) => {
        toastService.addToast({
          type: 'error',
          persist: true,
          message: error?.message || renderFilterErrorMessage(),
        });
      },
    });
  }, []);

  useEffect(() => {
    checkFilterChange(searchFilterValues);
  }, [searchFilterValues]);

  const handleSearch = (filterValues: TypeDeliveryAgencyTrackingFilterValues = searchFilterValues) => {
    if (filterValues.keyword.trim() === '' && !filterValues.deliveryDate) {
      dialogService.addDialog({
        children:
          '[운송장 번호, 출고번호, 판매처 주문번호, 수령자명, 주문자명, 배송예정일] 중 1개 이상 입력 후 검색해주세요.',
      });
      return;
    }

    const params: TypeGetShippingLabelsParams = {
      searchKeywordType: filterValues.keywordType?.value ?? '',
      searchKeyword: filterValues.keyword,
      ...addIf(formatDate(filterValues.deliveryDate, 'YYYY-MM-DD'), 'estimatedDeliveryDate'),
      ...addIf(filterValues.trackingStatus?.value, 'shippingLabelStatus'),
      ...addIf(
        filterValues.trackingPrintStatus?.value !== OPTION_ALL.value,
        'isPrinted',
        filterValues.trackingPrintStatus?.value === DELIVERY_AGENCY_TRACKING_PRINT_STATUS.Y.value
      ),
      ...addIf(filterValues.deliveryType?.value, 'deliveryType'),
      ...addIf(filterValues.courier?.value, 'courierCode'),
      cursor: filterValues.pagination.currentPage,
      limit: filterValues.pagination.pageSize,
    };

    requestShippingLabels(params, {
      onSuccess: ({ data, pagination }) => {
        setTrackingData(data);

        const updatedFilterValues = {
          ...filterValues,
          pagination: {
            ...filterValues.pagination,
            totalCount: pagination.listTotal,
            pageSize: pagination.listView,
            currentPage: pagination.cursor,
          },
        };
        setSearchFilterValues(updatedFilterValues);

        markDownloadReady({ filterValue: updatedFilterValues, hasData: data.length > 0 });
      },
      onError: (error) => {
        toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
      },
    });
  };

  const handleResetFilterValues = () => {
    setSearchFilterValues({
      ...INITIAL_SEARCH_FILTER_VALUES,
      pagination: searchFilterValues.pagination,
      keywordType: searchFilterOptions.searchKeywordTypes[0] ?? null,
    });
    setSearchFilterValuesStatus({
      keyword: 'default',
      deliveryDate: 'default',
    });
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
        pageSize: value.value,
      },
    });
  };

  const handleChangePage = (value: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: value },
    });
  };

  const handleChangeRowSelection = (selectedRows: IRowNode<TypeDeliveryAgencyShippingLabel>[]) => {
    const selectedItems = selectedRows.map((row) => row.data).filter((item) => !!item);
    setSelectedItems(selectedItems);
  };

  const handleOpenTrackingDetailModal = (order: TypeDeliveryAgencyShippingLabel) => {
    requestShippingLabelDetail(
      { shippingLabelId: order.shippingLabelId },
      {
        onSuccess: () => {
          trackingDetailModal.handleOpenModal();
        },
        onError: (error) => {
          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  const handlePrintShippingLabel = () => {
    const isAllPrinted = selectedItems.every((item) => item.isPrinted);
    const isNothingPrinted = selectedItems.every((item) => !item.isPrinted);

    let dialogContent = '';

    if (isAllPrinted) {
      dialogContent = '이미 출력한 운송장입니다.\n송장 출력을 진행하시겠습니까?';
    } else if (isNothingPrinted) {
      dialogContent = `운송장 ${selectedItems.length}개를 출력하시겠습니까?`;
    } else {
      const printedCount = selectedItems.filter((item) => item.isPrinted).length;
      dialogContent = `이미 출력한 운송장 ${printedCount}건이 포함되어 있습니다.\n송장 출력을 진행하시겠습니까?`;
    }

    dialogService.addDialog({
      type: 'confirm',
      children: dialogContent,
      onConfirm: () => {
        printShippingLabels(
          selectedItems.map((item) => item.shippingLabelId),
          () => setSelectedItems([])
        );
      },
    });
  };

  const printShippingLabels = (shippingLabelIds: number[], successCallback?: () => void | Promise<void>) =>
    requestShippingLabelsPrint(
      { shippingLabelIds },
      {
        onSuccess: () => {
          handleSearch();
          toastService.addToast({ message: '다운로드가 완료되었습니다.\n송장 출력기를 통해 송장을 출력해주세요.' });
          successCallback?.();
        },
        onError: (error) => {
          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  const handleCancelDelivery = () => {
    const unCancelableStatusCodes = new Set<TypeDeliveryAgencyShippingLabelStatus>([
      DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.ON_THE_WAY.value,
      DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_COMPLETED.value,
      DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_CANCELED.value,
      DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_FAILED.value,
    ]);

    const isNothingCancelable = selectedItems.every((item) =>
      unCancelableStatusCodes.has(item.shippingLabelStatus.code)
    );
    if (isNothingCancelable) {
      dialogService.addDialog({
        children: `선택한 운송장 ${selectedItems.length}건 모두 배송 취소 불가한 운송장입니다.`,
      });
      return;
    }

    const unCancelableItems = selectedItems.filter((item) =>
      unCancelableStatusCodes.has(item.shippingLabelStatus.code)
    );
    if (unCancelableItems.length > 0) {
      dialogService.addDialog({
        type: 'confirm',
        children: `취소 불가한 운송장이 ${unCancelableItems.length}개 포함되어 있습니다.\n불가 건 제외 후 취소를 진행하시겠습니까?\n취소 후 상태를 다시 되돌릴 수 없습니다.`,
        onConfirm: () => {
          const cancelableItems = selectedItems.filter(
            (item) => !unCancelableStatusCodes.has(item.shippingLabelStatus.code)
          );
          cancelDeliveryRequest(cancelableItems);
        },
      });
      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      children: `선택한 운송장 ${selectedItems.length}건을 배송 취소하시겠습니까?\n취소 후 상태를 다시 되돌릴 수 없습니다.`,
      onConfirm: () => {
        cancelDeliveryRequest(selectedItems);
      },
    });
  };

  const cancelDeliveryRequest = (items: TypeDeliveryAgencyShippingLabel[]) => {
    requestDeliveryCancel(
      {
        shippingLabelIds: items.map((item) => item.shippingLabelId),
      },
      {
        onSuccess: (data) => {
          if (data === null) {
            toastService.addToast({ message: '배송 취소되었습니다.' });
          } else {
            dialogService.addDialog({ children: data.message });
          }
          handleSearch();
          setSelectedItems([]);
        },
        onError: (error) => {
          if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
            dialogService.addDialog({ children: error?.message });
            return;
          }

          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  const handleConfirmExcelDownload = (reason: string) => {
    const params: TypePostShippingLabelsDownloadParams = {
      downloadReason: reason,
      input: {
        searchKeywordType: searchedFilter.keywordType?.value ?? '',
        searchKeyword: searchedFilter.keyword,
        ...addIf(formatDate(searchedFilter.deliveryDate, 'YYYY-MM-DD'), 'estimatedDeliveryDate'),
        ...addIf(searchedFilter.trackingStatus?.value, 'shippingLabelStatus'),
        ...addIf(
          searchedFilter.trackingPrintStatus?.value !== OPTION_ALL.value,
          'isPrinted',
          searchedFilter.trackingPrintStatus?.value === DELIVERY_AGENCY_TRACKING_PRINT_STATUS.Y.value
        ),
        ...addIf(searchedFilter.deliveryType?.value, 'deliveryType'),
        ...addIf(searchedFilter.courier?.value, 'courierCode'),
      },
    };

    requestShippingLabelsDownload(params, {
      onSuccess: () => {
        trackingExcelDownloadModal.handleConfirmModal();
        toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
      },
      onError: (error) => {
        toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
      },
    });
  };

  return (
    <PageWrapper>
      <PageTitle
        title="운송장 관리"
        breadcrumbs={BREAD_CRUMB[PATH.DELIVERY_AGENCY_TRACKING]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <>
          <DeliveryAgencyTrackingFilter
            options={searchFilterOptions}
            values={searchFilterValues}
            valuesStatus={searchFilterValuesStatus}
            onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
            onChangeStatus={(status) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...status }))}
            onSearch={() =>
              handleSearch({
                ...searchFilterValues,
                pagination: {
                  ...searchFilterValues.pagination,
                  currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
                },
              })
            }
            onReset={handleResetFilterValues}
          />
          <Divider color="gray100" />
        </>
      )}
      <DeliveryAgencyTrackingTable
        data={trackingData}
        pagination={searchFilterValues.pagination}
        selectedItems={selectedItems}
        isDownloadReady={isDownloadReady}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleChangePage}
        onChangeRowSelection={handleChangeRowSelection}
        onClickViewDetail={handleOpenTrackingDetailModal}
        onClickPrintShippingLabel={handlePrintShippingLabel}
        onClickTrackingExcelDownload={trackingExcelDownloadModal.handleOpenModal}
        onClickCancelDelivery={handleCancelDelivery}
        onClickCreateShippingLabel={createShippingLabelModal.handleOpenModal}
      />
      {trackingDetailModal.isOpen && trackingDetailData && (
        <DeliveryAgencyTrackingDetailModal
          open={trackingDetailModal.isOpen}
          data={trackingDetailData}
          onClose={trackingDetailModal.handleCloseModal}
        />
      )}
      {trackingExcelDownloadModal.isOpen && (
        <DeliveryAgencyExcelDownloadModal
          type="운송장"
          open={trackingExcelDownloadModal.isOpen}
          onCancel={trackingExcelDownloadModal.handleCloseModal}
          onConfirm={(reason) => handleConfirmExcelDownload(reason)}
        />
      )}
      {createShippingLabelModal.isOpen && (
        <DeliveryAgencyTrackingCreateShippingLabelModal
          open={createShippingLabelModal.isOpen}
          onPrintShippingLabels={printShippingLabels}
          onCancel={createShippingLabelModal.handleCloseModal}
          onConfirm={createShippingLabelModal.handleConfirmModal}
        />
      )}
    </PageWrapper>
  );
};

export default DeliveryAgencyTrackingPage;
